import {injectable} from "inversify";
import Scene = Phaser.Scene;
import BaseSound = Phaser.Sound.BaseSound;
import TextUtils from "@/utils/text-utils";
import {Tr} from "@/i18n/i18n";
import {Fonts} from "@/constants/font";
import OnceCaller from "@/utils/once-caller";
import Sprite = Phaser.GameObjects.Sprite;
import Text = Phaser.GameObjects.Text;
import {Player} from "@/store/player/types";
import PlayerStore from "@/store/player";
import {Keys} from "@/constants/key";
import SoundUtils from "@/utils/sound-utils";
import {NewGiftPanel} from "@/scenes.ui/new-gift-panel";
import GiftStore from "@/store/gift";
import {NewGiftsDto} from "@/store/gift/types";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";

@injectable()
export class GiftService {

    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;
    private giftButton: Sprite | undefined;
    private giftTitle: Text | undefined;

    private clickSound: BaseSound | undefined;
    private completedSound: BaseSound | undefined;

    private profileCallback: ((playerId: number) => unknown) | undefined;

    public initGiftButton(scene: Scene, canvas: HTMLCanvasElement, clickSound: BaseSound | undefined,
                          completedSound: BaseSound | undefined, profileCallback: (playerId: number) => unknown): void {
        this.scene = scene;
        this.canvas = canvas;
        this.clickSound = clickSound;
        this.completedSound = completedSound;
        this.profileCallback = profileCallback;

        this.updateGiftButton();
    }

    public updateGiftButton() {
        let player: Player = PlayerStore.getPlayer();

        let self = this;
        if (player.haveNewGift) {
            this.addGiftButton(40, 370, Keys.ATLAS_PROFILE, Keys.NEW_GIFT, () => {
                GiftStore.getNewGifts().then((ng: NewGiftsDto) => {
                    this.openNewGiftPanel(ng);
                });
            });
        } else {
            this.destroyGiftButton();
        }
    }

    private openNewGiftPanel(ng: NewGiftsDto) {
        let self = this;
        let panel: NewGiftPanel = new NewGiftPanel(self.scene!, self.canvas!, ng, (giftId: number) => {
            GiftStore.approve(giftId).then((ngg: NewGiftsDto) => {
                new FeedbackPanel(self.scene!, Tr('gft.rcvd'), undefined, true).show();
                if (ngg.count > 0) {
                    self.openNewGiftPanel(ngg);
                } else {
                    self.destroyGiftButton();
                }
            });
        }, () => {
            GiftStore.approveAll().then(() => {
                self.destroyGiftButton();
                new FeedbackPanel(self.scene!, Tr('gft.all.rcvd'), undefined, true).show();
            });
        }, (playerId: number) => {
            self.profileCallback!(playerId);
        });
        SoundUtils.play(self.clickSound);
        panel.openAnimated();
    }

    private addGiftButton(x: number, y: number, imageAtlas: string, imageKey: string, callback: () => unknown): Sprite {
        this.destroyGiftButton();
        this.giftButton = SpriteUtils.addSprite(this.scene!, x, y, imageAtlas, imageKey);
        this.giftButton.setDepth(1000);
        this.giftTitle = TextUtils.addTextAdvanced(this.scene!, Tr('gft'), this.giftButton.x / CU.S, this.giftButton.y / CU.S + 45,
            '#ffffff', 9, 'normal', 90, 'center', 1, Fonts.ARIAL_MT);
        this.giftTitle.setStroke('#000000', 2);
        this.giftTitle.setDepth(1000);
        this.giftButton.setInteractive().on('pointerup', () => {
            OnceCaller.doOnce('gift_' + imageKey, callback);
        }, this.giftButton);

        return this.giftButton;
    }

    private destroyGiftButton() {
        if (this.giftButton) {
            this.giftButton.setVisible(false);
            this.giftButton.destroy();
            this.giftButton = undefined;
        }
        if (this.giftTitle) {
            this.giftTitle.setVisible(false);
            this.giftTitle.destroy();
            this.giftTitle = undefined;
        }
    }
}
